import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link } from "react-router-dom";
import PlayerProfileModal from "../components/PlayerProfileModal";
import PlayerTraitBadges from "../components/PlayerTraitBadges";
import { listLeagues, listOrganizations, listPlayerParents, listRosterMemberships, listTeamPlayers, listTeams, listUserAssignments, listUsers as listAppUsers } from "../graphql/queries";
import { createPlayer, createPlayerParent, createUser, deletePlayer, deletePlayerParent } from "../graphql/mutations";
import { listPlayersWithTraits } from "../graphql/playerTraitQueries";
import { formatApiError } from "../lib/formatApiError";
import { adminPost } from "../lib/adminQueries";
import { buildAdminScope } from "../lib/adminScope";
import { buildCoachTeamIds, buildPlayerTeamIds, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import type { ActiveRoleScope } from "../lib/roleContext";

interface Player {
  id: string;
  name: string;
  teamId: string;
  dateOfBirth?: string | null;
  town?: string | null;
  state?: string | null;
  pitcher?: boolean | null;
  catcher?: boolean | null;
  battingHandedness?: string | null;
  throwingHandedness?: string | null;
}

interface Team {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  coachUsernames?: string[] | null;
}

interface Organization {
  id: string;
  name: string;
  admins?: string[] | null;
}

interface League {
  id: string;
  name: string;
  organizationId: string;
  admins?: string[] | null;
}

interface TeamPlayer {
  id: string;
  teamID: string;
  playerID: string;
}

interface PlayerParent {
  id: string;
  playerId: string;
  parentEmail: string;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

interface AppUser {
  id: string;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  roles?: string[] | null;
}

interface PlayersPageProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

type SortKey = "name" | "age" | "dob" | "parentEmail" | "teams" | "town" | "state";
type SortDirection = "asc" | "desc";

const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === "\"") {
      if (inQuotes && line[i + 1] === "\"") {
        current += "\"";
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current.trim());
  return out;
};

const normalizeHeader = (value: string): string => value.trim().toLowerCase().replace(/[\s_-]+/g, "");

const PlayersPage: React.FC<PlayersPageProps> = ({ userRoles, activeRoleScope }) => {
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [playerParents, setPlayerParents] = useState<PlayerParent[]>([]);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [newName, setNewName] = useState("");
  const [newTeamId, setNewTeamId] = useState("");
  const [newDateOfBirth, setNewDateOfBirth] = useState("");
  const [newTown, setNewTown] = useState("");
  const [newState, setNewState] = useState("");
  const [newParentEmail, setNewParentEmail] = useState("");
  const [newPitcher, setNewPitcher] = useState(false);
  const [newCatcher, setNewCatcher] = useState(false);
  const [newBattingHandedness, setNewBattingHandedness] = useState("");
  const [newThrowingHandedness, setNewThrowingHandedness] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterTeamId, setFilterTeamId] = useState("");
  const [filterState, setFilterState] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [bulkStatusMessage, setBulkStatusMessage] = useState("");
  const [bulkErrorMessage, setBulkErrorMessage] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [activePlayerModalId, setActivePlayerModalId] = useState<string | null>(null);
  const [invitingParentEmail, setInvitingParentEmail] = useState("");

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues, teams, players, teamPlayers, rosterMemberships }),
    [leagues, organizations, players, rosterMemberships, teamPlayers, teams, userRoles, username]
  );
  const canManage = adminScope.isGlobalAdmin || adminScope.isScopedCommish || userRoles.includes("Coach");
  const isPrivileged = adminScope.isGlobalAdmin;
  const hasScopedCommishRole = adminScope.isScopedCommish;
  const hasCoachRole = userRoles.includes("Coach");
  const hasParentRole = userRoles.includes("Parent") || userRoles.includes("Parents");
  const canBulkUpload = userRoles.includes("SuperAdmin") || userRoles.includes("Commish") || userRoles.includes("Coach");

  const teamById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const team of teams) {
      out[team.id] = team.name;
    }
    return out;
  }, [teams]);

  const teamRecordById = useMemo(() => {
    const out: Record<string, Team> = {};
    for (const team of teams) {
      out[team.id] = team;
    }
    return out;
  }, [teams]);

  const appUserByEmail = useMemo(() => {
    const out: Record<string, AppUser> = {};
    for (const user of appUsers) {
      const email = (user.email ?? "").trim().toLowerCase();
      if (email && !out[email]) {
        out[email] = user;
      }
    }
    return out;
  }, [appUsers]);

  const parentByPlayerId = useMemo(() => {
    const out: Record<string, PlayerParent> = {};
    for (const parent of playerParents) {
      if (!out[parent.playerId]) {
        out[parent.playerId] = parent;
      }
    }
    return out;
  }, [playerParents]);

  const parentScopedPlayerIds = useMemo(() => {
    const activeAsParent = activeRoleScope?.targetType === "parent";
    if ((!hasParentRole && !activeAsParent) || (isPrivileged && !activeAsParent) || !userEmail.trim()) {
      return new Set<string>();
    }
    const normalized = userEmail.trim().toLowerCase();
    return new Set<string>(
      playerParents
        .filter((link) => (link.parentEmail ?? "").trim().toLowerCase() === normalized)
        .map((link) => link.playerId)
    );
  }, [activeRoleScope, hasParentRole, isPrivileged, playerParents, userEmail]);

  const teamIdsByPlayerId = useMemo(
    () => buildPlayerTeamIds({ players, teamPlayers, rosterMemberships }),
    [players, rosterMemberships, teamPlayers]
  );

  const coachTeamIds = useMemo(
    () => (hasCoachRole && !isPrivileged ? buildCoachTeamIds({ username, teams, userAssignments }) : new Set<string>()),
    [hasCoachRole, isPrivileged, teams, userAssignments, username]
  );

  const scopedPlayers = useMemo(() => {
    if (activeRoleScope?.targetType === "team" && activeRoleScope.teamId) {
      return players.filter((player) => (teamIdsByPlayerId[player.id] ?? new Set<string>()).has(activeRoleScope.teamId as string));
    }
    if (activeRoleScope?.targetType === "parent") {
      return players.filter((player) => parentScopedPlayerIds.has(player.id));
    }
    if (activeRoleScope?.targetType === "organization" && activeRoleScope.organizationId) {
      return players.filter((player) =>
        [...(teamIdsByPlayerId[player.id] ?? new Set<string>())].some(
          (teamId) => teamRecordById[teamId]?.organizationId === activeRoleScope.organizationId
        )
      );
    }
    if (activeRoleScope?.targetType === "league" && activeRoleScope.leagueId) {
      return players.filter((player) =>
        [...(teamIdsByPlayerId[player.id] ?? new Set<string>())].some(
          (teamId) => teamRecordById[teamId]?.leagueId === activeRoleScope.leagueId
        )
      );
    }
    if (isPrivileged) {
      return players;
    }
    const allowedIds = new Set<string>([...parentScopedPlayerIds]);
    if (hasScopedCommishRole) {
      for (const playerId of adminScope.managedPlayerIds) {
        allowedIds.add(playerId);
      }
    }
    if (hasCoachRole) {
      for (const player of players) {
        const memberships = teamIdsByPlayerId[player.id] ?? new Set<string>();
        if ([...memberships].some((teamId) => coachTeamIds.has(teamId))) {
          allowedIds.add(player.id);
        }
      }
    }
    if (!hasCoachRole && !hasParentRole && !hasScopedCommishRole) {
      return players;
    }
    return players.filter((player) => allowedIds.has(player.id));
  }, [activeRoleScope, adminScope.managedPlayerIds, coachTeamIds, hasCoachRole, hasParentRole, hasScopedCommishRole, isPrivileged, parentScopedPlayerIds, players, teamIdsByPlayerId, teamRecordById]);

  const scopedTeams = useMemo(() => {
    if (activeRoleScope?.targetType === "team" && activeRoleScope.teamId) {
      return teams.filter((team) => team.id === activeRoleScope.teamId);
    }
    if (activeRoleScope?.targetType === "organization" && activeRoleScope.organizationId) {
      return teams.filter((team) => team.organizationId === activeRoleScope.organizationId);
    }
    if (activeRoleScope?.targetType === "league" && activeRoleScope.leagueId) {
      return teams.filter((team) => team.leagueId === activeRoleScope.leagueId);
    }
    if (activeRoleScope?.targetType === "parent") {
      const parentTeamIds = new Set<string>();
      for (const player of players) {
        if (!parentScopedPlayerIds.has(player.id)) {
          continue;
        }
        for (const teamId of teamIdsByPlayerId[player.id] ?? new Set<string>()) {
          parentTeamIds.add(teamId);
        }
      }
      return teams.filter((team) => parentTeamIds.has(team.id));
    }
    if (isPrivileged) {
      return teams;
    }
    const teamIds = new Set<string>();
    if (hasScopedCommishRole) {
      for (const teamId of adminScope.managedTeamIds) {
        teamIds.add(teamId);
      }
    }
    if (hasCoachRole) {
      for (const coachTeamId of coachTeamIds) {
        teamIds.add(coachTeamId);
      }
    }
    for (const player of players) {
      if (!parentScopedPlayerIds.has(player.id)) {
        continue;
      }
      for (const teamId of teamIdsByPlayerId[player.id] ?? new Set<string>()) {
        teamIds.add(teamId);
      }
    }
    if (!hasCoachRole && !hasParentRole && !hasScopedCommishRole) {
      return teams;
    }
    return teams.filter((team) => teamIds.has(team.id));
  }, [activeRoleScope, adminScope.managedTeamIds, coachTeamIds, hasCoachRole, hasParentRole, hasScopedCommishRole, isPrivileged, parentScopedPlayerIds, players, teamIdsByPlayerId, teams]);

  const computeAge = (dateOfBirth?: string | null): number | null => {
    if (!dateOfBirth) {
      return null;
    }
    const dob = new Date(`${dateOfBirth}T00:00:00`);
    if (Number.isNaN(dob.getTime())) {
      return null;
    }
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const beforeBirthday =
      now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
    if (beforeBirthday) {
      age -= 1;
    }
    return Math.max(0, age);
  };

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      setUserEmail((authUser?.attributes?.email ?? "").toLowerCase());
      const [playerRes, teamRes, organizationRes, leagueRes, teamPlayerRes, rosterMembershipRes, userAssignmentsRes, playerParentRes, appUsersRes] = await Promise.all([
        API.graphql(graphqlOperation(listPlayersWithTraits, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 2000 })),
        API.graphql(graphqlOperation(listAppUsers, { limit: 3000 }))
      ]);

      setPlayers(((playerRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean));
      setTeams(((teamRes as any)?.data?.listTeams?.items ?? []).filter(Boolean));
      setOrganizations(((organizationRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setLeagues(((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean));
      setTeamPlayers(((teamPlayerRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean));
      setRosterMemberships(((rosterMembershipRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean));
      setUserAssignments(((userAssignmentsRes as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean));
      setPlayerParents(((playerParentRes as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean));
      setAppUsers(((appUsersRes as any)?.data?.listUsers?.items ?? []).filter(Boolean));
    } catch (error) {
      console.error("Error loading players:", error);
      if (!silent) {
        setErrorMessage(`Failed to load players: ${formatApiError(error)}`);
      }
    }
  };

  const inviteParentUser = async (parentEmail: string) => {
    const normalizedEmail = parentEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }
    if (appUserByEmail[normalizedEmail]) {
      setErrorMessage(`A user profile already exists for ${normalizedEmail}.`);
      return;
    }

    setInvitingParentEmail(normalizedEmail);
    setErrorMessage("");

    try {
      await adminPost("/createUser", {
        username: normalizedEmail,
        email: normalizedEmail,
        userAttributes: [{ Name: "email", Value: normalizedEmail }]
      });
      await adminPost("/addUserToGroup", {
        username: normalizedEmail,
        groupname: "Parent"
      });

      await API.graphql(
        graphqlOperation(createUser, {
          input: {
            id: normalizedEmail,
            email: normalizedEmail,
            givenName: null,
            familyName: null,
            phone: null,
            safetyCertified: false,
            abcaId: null,
            roles: ["Parent"]
          }
        })
      );

      setBulkStatusMessage(`Invite sent to ${normalizedEmail}. The player-parent association remains linked by email.`);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to invite parent ${normalizedEmail}: ${formatApiError(error)}`);
    } finally {
      setInvitingParentEmail("");
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newTeamId) {
      return;
    }
    if (hasScopedCommishRole && !adminScope.managedTeamIds.has(newTeamId)) {
      setErrorMessage("Commish can only add players to assigned teams.");
      return;
    }
    if (hasCoachRole && !isPrivileged && !coachTeamIds.has(newTeamId)) {
      setErrorMessage("Coach can only add players to their own team.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(createPlayer, {
          input: {
            name: newName.trim(),
            teamId: newTeamId,
            dateOfBirth: newDateOfBirth || null,
            town: newTown.trim() || null,
            state: newState.trim().toUpperCase() || null,
            pitcher: newPitcher,
            catcher: newCatcher,
            battingHandedness: newBattingHandedness || null,
            throwingHandedness: newThrowingHandedness || null
          }
        })
      );
      if (!result?.data?.createPlayer) {
        throw new Error(formatApiError(result));
      }
      const created = result.data.createPlayer as Player;
      if (newParentEmail.trim()) {
        await API.graphql(
          graphqlOperation(createPlayerParent, {
            input: {
              playerId: created.id,
              parentEmail: newParentEmail.trim().toLowerCase()
            }
          })
        );
      }
      setNewName("");
      setNewTeamId("");
      setNewDateOfBirth("");
      setNewTown("");
      setNewState("");
      setNewParentEmail("");
      setNewPitcher(false);
      setNewCatcher(false);
      setNewBattingHandedness("");
      setNewThrowingHandedness("");
      setShowAddDialog(false);
      await loadData(true);
    } catch (error) {
      console.error("Error creating player:", error);
      setErrorMessage(`Failed to create player: ${formatApiError(error)}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this player?")) {
      return;
    }

    try {
      setErrorMessage("");
      const player = players.find((item) => item.id === id);
      if (player && hasScopedCommishRole && !adminScope.canManagePlayer(player, teamIdsByPlayerId[player.id])) {
        setErrorMessage("Commish can only delete assigned players.");
        return;
      }
      const result: any = await API.graphql(graphqlOperation(deletePlayer, { input: { id } }));
      if (!result?.data?.deletePlayer) {
        throw new Error(formatApiError(result));
      }
      const parents = playerParents.filter((item) => item.playerId === id);
      await Promise.all(
        parents.map((parent) =>
          API.graphql(graphqlOperation(deletePlayerParent, { input: { id: parent.id } }))
        )
      );
      await loadData(true);
    } catch (error) {
      console.error("Error deleting player:", error);
      setErrorMessage(`Failed to delete player: ${formatApiError(error)}`);
    }
  };

  const handleBulkCsvUpload = async (file: File) => {
    if (!canBulkUpload) {
      setBulkErrorMessage("You do not have permission to bulk upload players.");
      return;
    }
    setBulkLoading(true);
    setBulkErrorMessage("");
    setBulkStatusMessage("");
    setErrorMessage("");

    try {
      const raw = await file.text();
      const lines = raw
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (lines.length < 2) {
        throw new Error("CSV must include a header row and at least one data row.");
      }

      const headerCells = splitCsvLine(lines[0]).map((value) => normalizeHeader(value));
      const headerMap: Record<string, number> = {};
      headerCells.forEach((header, index) => {
        headerMap[header] = index;
      });

      const getCell = (cells: string[], aliases: string[]) => {
        for (const alias of aliases) {
          const idx = headerMap[alias];
          if (typeof idx === "number") {
            return (cells[idx] ?? "").trim();
          }
        }
        return "";
      };

      if (typeof headerMap.name !== "number" && typeof headerMap.player !== "number" && typeof headerMap.playername !== "number") {
        throw new Error("CSV is missing a player name column. Use one of: name, player, playerName.");
      }

      const teamByIdMap = new Map(scopedTeams.map((team) => [team.id, team]));
      const teamByNameMap = new Map(scopedTeams.map((team) => [team.name.trim().toLowerCase(), team]));
      const existingByNameAndTeam = new Set(players.map((player) => `${player.name.trim().toLowerCase()}|${player.teamId}`));

      let createdCount = 0;
      const rowErrors: string[] = [];

      for (let i = 1; i < lines.length; i += 1) {
        const rowNumber = i + 1;
        const cells = splitCsvLine(lines[i]);
        const name = getCell(cells, ["name", "player", "playername"]).trim();
        const teamIdValue = getCell(cells, ["teamid", "team"]);
        const teamNameValue = getCell(cells, ["teamname", "team"]);
        const dob = getCell(cells, ["dateofbirth", "dob", "birthdate"]);
        const town = getCell(cells, ["town", "city"]);
        const state = getCell(cells, ["state", "province"]);
        const parentEmail = getCell(cells, ["parentemail", "parent"]);

        if (!name) {
          rowErrors.push(`Row ${rowNumber}: missing player name.`);
          continue;
        }

        let resolvedTeam: Team | undefined;
        if (teamIdValue && teamByIdMap.has(teamIdValue)) {
          resolvedTeam = teamByIdMap.get(teamIdValue);
        } else if (teamNameValue && teamByNameMap.has(teamNameValue.trim().toLowerCase())) {
          resolvedTeam = teamByNameMap.get(teamNameValue.trim().toLowerCase());
        } else if (!teamIdValue && !teamNameValue && hasCoachRole && !isPrivileged && scopedTeams.length === 1) {
          resolvedTeam = scopedTeams[0];
        }

        if (!resolvedTeam) {
          rowErrors.push(`Row ${rowNumber}: team not found or not allowed (${teamIdValue || teamNameValue || "empty"}).`);
          continue;
        }

        if (hasCoachRole && !isPrivileged && !coachTeamIds.has(resolvedTeam.id)) {
          rowErrors.push(`Row ${rowNumber}: coach can only add players to coached teams.`);
          continue;
        }

        const dupKey = `${name.toLowerCase()}|${resolvedTeam.id}`;
        if (existingByNameAndTeam.has(dupKey)) {
          rowErrors.push(`Row ${rowNumber}: player "${name}" already exists for team "${resolvedTeam.name}".`);
          continue;
        }

        try {
          const createRes: any = await API.graphql(
            graphqlOperation(createPlayer, {
              input: {
                name,
                teamId: resolvedTeam.id,
                dateOfBirth: dob || null,
                town: town || null,
                state: state ? state.toUpperCase() : null
              }
            })
          );
          const created = createRes?.data?.createPlayer as Player | undefined;
          if (!created?.id) {
            rowErrors.push(`Row ${rowNumber}: failed to create player "${name}".`);
            continue;
          }

          if (parentEmail) {
            await API.graphql(
              graphqlOperation(createPlayerParent, {
                input: {
                  playerId: created.id,
                  parentEmail: parentEmail.toLowerCase()
                }
              })
            );
          }

          existingByNameAndTeam.add(dupKey);
          createdCount += 1;
        } catch (error) {
          rowErrors.push(`Row ${rowNumber}: ${formatApiError(error)}`);
        }
      }

      await loadData(true);
      if (rowErrors.length > 0) {
        setBulkErrorMessage(rowErrors.slice(0, 12).join(" | "));
      }
      setBulkStatusMessage(`Bulk upload complete. Created ${createdCount} player${createdCount === 1 ? "" : "s"}${rowErrors.length ? `, ${rowErrors.length} row error(s)` : ""}.`);
    } catch (error) {
      setBulkErrorMessage(`Bulk upload failed: ${formatApiError(error)}`);
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const rows = useMemo(() => {
    const normalizedSearch = filterText.trim().toLowerCase();
    const normalizedState = filterState.trim().toLowerCase();

    const filtered = scopedPlayers.filter((player) => {
      const teamNames = [...(teamIdsByPlayerId[player.id] ?? new Set<string>())]
        .map((teamId) => teamById[teamId] ?? "Unknown team")
        .join(" ");
      const parentEmail = parentByPlayerId[player.id]?.parentEmail ?? "";
      const haystack = `${player.name} ${teamNames} ${player.town ?? ""} ${player.state ?? ""} ${parentEmail}`.toLowerCase();
      if (normalizedSearch && !haystack.includes(normalizedSearch)) {
        return false;
      }
      if (filterTeamId && !(teamIdsByPlayerId[player.id] ?? new Set<string>()).has(filterTeamId)) {
        return false;
      }
      if (normalizedState && (player.state ?? "").toLowerCase() !== normalizedState) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const aTeams = [...(teamIdsByPlayerId[a.id] ?? new Set<string>())]
        .map((teamId) => teamById[teamId] ?? "Unknown team")
        .join(", ");
      const bTeams = [...(teamIdsByPlayerId[b.id] ?? new Set<string>())]
        .map((teamId) => teamById[teamId] ?? "Unknown team")
        .join(", ");
      const aParentEmail = parentByPlayerId[a.id]?.parentEmail ?? "";
      const bParentEmail = parentByPlayerId[b.id]?.parentEmail ?? "";
      const aAge = computeAge(a.dateOfBirth) ?? -1;
      const bAge = computeAge(b.dateOfBirth) ?? -1;

      let compare = 0;
      switch (sortKey) {
        case "age":
          compare = aAge - bAge;
          break;
        case "dob":
          compare = (a.dateOfBirth ?? "").localeCompare(b.dateOfBirth ?? "");
          break;
        case "parentEmail":
          compare = aParentEmail.localeCompare(bParentEmail);
          break;
        case "teams":
          compare = aTeams.localeCompare(bTeams);
          break;
        case "town":
          compare = (a.town ?? "").localeCompare(b.town ?? "");
          break;
        case "state":
          compare = (a.state ?? "").localeCompare(b.state ?? "");
          break;
        case "name":
        default:
          compare = a.name.localeCompare(b.name);
          break;
      }
      return sortDirection === "asc" ? compare : -compare;
    });

    return sorted;
  }, [scopedPlayers, teamIdsByPlayerId, teamById, parentByPlayerId, filterText, filterTeamId, filterState, sortKey, sortDirection]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Players</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {(canManage || canBulkUpload) && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          {canManage && (
            <button onClick={() => setShowAddDialog(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              Add Player
            </button>
          )}
          {canBulkUpload && (
            <button onClick={() => setShowBulkDialog(true)} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              Bulk Load CSV
            </button>
          )}
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter by player, email, team, town, state"
        />
        <select value={filterTeamId} onChange={(e) => setFilterTeamId(e.target.value)} className="border px-3 py-2 rounded">
          <option value="">All teams</option>
          {scopedTeams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
        <input
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="State filter (ex. NY)"
        />
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>Player</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("age")}>Age</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("dob")}>DOB</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("parentEmail")}>Parent Email</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("teams")}>Teams</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("town")}>Town</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("state")}>State</button></th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={canManage ? 8 : 7} className="text-gray-600">No players found.</td>
              </tr>
            )}
            {rows.map((player) => {
              const teamsForPlayer = [...(teamIdsByPlayerId[player.id] ?? new Set<string>())]
                .map((teamId) => ({ id: teamId, name: teamById[teamId] ?? "Unknown team" }))
                .sort((a, b) => a.name.localeCompare(b.name));
              const parentEmail = parentByPlayerId[player.id]?.parentEmail ?? "";
              const age = computeAge(player.dateOfBirth);

              return (
                <tr key={player.id}>
                  <td>
                    <button className="table-sort" onClick={() => setActivePlayerModalId(player.id)}>
                      {player.name}
                    </button>
                    <div className="mt-1">
                      <PlayerTraitBadges player={player} compact />
                    </div>
                  </td>
                  <td>{age ?? "-"}</td>
                  <td>{player.dateOfBirth ?? "-"}</td>
                  <td>
                    {parentEmail ? (
                      <a href={`mailto:${parentEmail}`}>{parentEmail}</a>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  <td>
                    <div className="bubble-pill-wrap">
                      {teamsForPlayer.map((team) => (
                        <span key={`${player.id}-${team.id}`} className="bubble-pill">{team.name}</span>
                      ))}
                    </div>
                  </td>
                  <td>{player.town ?? "-"}</td>
                  <td>{player.state ?? "-"}</td>
                  {canManage && (
                    <td>
                      <div className="flex gap-2">
                        {parentEmail && !appUserByEmail[parentEmail.trim().toLowerCase()] ? (
                          <button
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            disabled={invitingParentEmail === parentEmail.trim().toLowerCase()}
                            onClick={() => inviteParentUser(parentEmail)}
                          >
                            {invitingParentEmail === parentEmail.trim().toLowerCase() ? "Inviting..." : "Invite Parent"}
                          </button>
                        ) : null}
                        <Link to={`/admin/players/${player.id}`} className="bg-yellow-500 text-white px-3 py-1 rounded">
                          Edit
                        </Link>
                        <button onClick={() => handleDelete(player.id)} className="bg-red-600 text-white px-3 py-1 rounded">
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canManage && showAddDialog && (
        <div className="app-dialog-backdrop" onClick={() => setShowAddDialog(false)}>
          <div className="app-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="app-dialog__header">
              <h2>Add Player</h2>
              <button className="app-dialog__close" onClick={() => setShowAddDialog(false)}>x</button>
            </div>
            <div className="app-dialog__body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New player name"
                  className="border px-3 py-2 rounded"
                />
                <select value={newTeamId} onChange={(e) => setNewTeamId(e.target.value)} className="border px-3 py-2 rounded">
                  <option value="">Select team</option>
                  {scopedTeams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={newDateOfBirth}
                  onChange={(e) => setNewDateOfBirth(e.target.value)}
                  className="border px-3 py-2 rounded"
                  title="Date of birth"
                />
                <input
                  value={newTown}
                  onChange={(e) => setNewTown(e.target.value)}
                  placeholder="Town"
                  className="border px-3 py-2 rounded"
                />
                <input
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  placeholder="State (ex. NY)"
                  className="border px-3 py-2 rounded"
                />
                <input
                  value={newParentEmail}
                  onChange={(e) => setNewParentEmail(e.target.value)}
                  placeholder="Parent Email"
                  className="border px-3 py-2 rounded"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={newPitcher} onChange={(e) => setNewPitcher(e.target.checked)} />
                  Can pitch
                </label>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={newCatcher} onChange={(e) => setNewCatcher(e.target.checked)} />
                  Can catch
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  <span>Batting Hand</span>
                  <select value={newBattingHandedness} onChange={(e) => setNewBattingHandedness(e.target.value)} className="border px-3 py-2 rounded">
                    <option value="">Unknown</option>
                    <option value="L">L</option>
                    <option value="R">R</option>
                    <option value="B">B</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  <span>Throwing Hand</span>
                  <select value={newThrowingHandedness} onChange={(e) => setNewThrowingHandedness(e.target.value)} className="border px-3 py-2 rounded">
                    <option value="">Unknown</option>
                    <option value="L">L</option>
                    <option value="R">R</option>
                  </select>
                </label>
              </div>
            </div>
            <div className="app-dialog__footer">
              <button onClick={() => setShowAddDialog(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">
                Cancel
              </button>
              <button onClick={handleCreate} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {canBulkUpload && showBulkDialog && (
        <div className="app-dialog-backdrop" onClick={() => setShowBulkDialog(false)}>
          <div className="app-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="app-dialog__header">
              <h2>Bulk Load Players (CSV)</h2>
              <button className="app-dialog__close" onClick={() => setShowBulkDialog(false)}>x</button>
            </div>
            <div className="app-dialog__body">
              <p className="text-sm text-gray-600 mb-2">
                Columns: <code>name</code>, <code>teamId</code> or <code>teamName</code>, optional <code>dob</code>, <code>town</code>, <code>state</code>, <code>parentEmail</code>.
              </p>
              <p className="text-sm mb-2">
                <a href="/sample-players-upload.csv" download>Download Sample CSV</a>
              </p>
              {bulkErrorMessage && <p className="mb-2" style={{ color: "#c1121f" }}>{bulkErrorMessage}</p>}
              {bulkStatusMessage && <p className="mb-2" style={{ color: "#166534" }}>{bulkStatusMessage}</p>}
              <input
                type="file"
                accept=".csv,text/csv"
                className="border px-3 py-2 rounded"
                disabled={bulkLoading}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  await handleBulkCsvUpload(file);
                  event.currentTarget.value = "";
                }}
              />
            </div>
            <div className="app-dialog__footer">
              <button onClick={() => setShowBulkDialog(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {activePlayerModalId && (
        <PlayerProfileModal
          playerId={activePlayerModalId}
          userRoles={userRoles}
          onClose={() => setActivePlayerModalId(null)}
        />
      )}
    </div>
  );
};

export default PlayersPage;
